import { styled, useTheme } from '@mui/material';
import { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { RouteFn } from '@/hooks/use-flow';

export const SuccessAnimation = ({ route }: { route: RouteFn }) => {
  const animationContainer = useRef<HTMLCanvasElement>(null);
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const inverseColor = theme.palette.background.inverse;

  useEffect(() => {
    if (animationContainer.current) {
      const confettiRef = confetti.create(animationContainer.current, { resize: true });
      setTimeout(
        () =>
          confettiRef({
            particleCount: 70,
            startVelocity: 35,
            ticks: 40,
            spread: 125,
            angle: 90,
            shapes: ['square'],
            gravity: 2.5,
            scalar: 1.5,
            origin: {
              x: 0.5,
              y: 0.55,
            },
            colors: [primaryColor, primaryColor, inverseColor],
          }),
        1000
      );
    }
  }, [primaryColor, inverseColor, route]);

  return (
    <StyledContainer>
      <StyledCanvas ref={animationContainer} />
      <StyledCheckmark>
        <svg
          id='successAnimation'
          className='animated'
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 70 70'
        >
          <circle
            className='successAnimationResult successAnimationScale'
            cx='35'
            cy='35'
            r='24'
            stroke={primaryColor}
            strokeWidth='4'
            strokeLinecap='round'
            fill={primaryColor}
          />
          <polyline
            className='successAnimationResult successAnimationScale'
            stroke={theme.palette.background.default}
            strokeWidth='4'
            points='24 34 34 43 47 27'
            fill='transparent'
          />
          <circle
            id='successAnimationCircle'
            cx='35'
            cy='35'
            r='24'
            stroke={primaryColor}
            strokeWidth='4'
            strokeLinecap='round'
            fill='transparent'
          />
          <polyline
            id='successAnimationCheck'
            stroke={primaryColor}
            strokeWidth='4'
            points='24 34 34 43 47 27'
            fill='transparent'
          />
        </svg>
      </StyledCheckmark>
    </StyledContainer>
  );
};

const StyledContainer = styled('div')`
  min-height: 450px;
  min-width: 375px;
  width: 100%;
  height: 100%;
  position: relative;
`;
const StyledCheckmark = styled('div')`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  #successAnimation {
    height: 140px;
    width: 140px;
  }

  #successAnimationCircle {
    stroke-dasharray: 151px 151px;
    stroke: ${({ theme }) => theme.palette.primary.main};
  }

  #successAnimationCheck {
    stroke-dasharray: 36px 36px;
    stroke: ${({ theme }) => theme.palette.primary.main};
  }

  #successAnimationResult {
    opacity: 0;
  }

  .animated {
    animation: 1s ease-out 0s 1 both scaleAnimation;
  }
  .animated #successAnimationCircle {
    animation: 1s cubic-bezier(0.77, 0, 0.175, 1) 0s 1 both drawCircle, 0.3s linear 0.9s 1 both fadeOut;
  }
  .animated #successAnimationCheck {
    animation: 1s cubic-bezier(0.77, 0, 0.175, 1) 0s 1 both drawCheck, 0.3s linear 0.9s 1 both fadeOut;
  }
  .animated .successAnimationResult {
    transform-origin: center;
    animation: 0.3s linear 0.9s both fadeIn, bounce 0.5s ease-in-out 1s both;
  }

  @keyframes scaleAnimation {
    0% {
      opacity: 0;
      transform: scale(1.5);
    }
    75% {
      opacity: 1;
      transform: scale(1);
    }
    75%, 100% {
      transform: none;
    }
  }

  @keyframes bounce {
    0%, 100% {
      transform: none;
    }

    50% {
      transform: scale(1.3);
    }
  }

  @keyframes drawCircle {
    0% {
      stroke-dashoffset: 151px;
    }
    100% {
      stroke-dashoffset: 0;
    }
  }

  @keyframes drawCheck {
    0% {
      stroke-dashoffset: 36px;
    }
    100% {
      stroke-dashoffset: 0;
    }
  }

  @keyframes fadeOut {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
}`;

const StyledCanvas = styled('canvas')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;
